import React, { FC, Suspense, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { graphql } from 'relay-runtime';
import { getAIStudioToolScopeKey, getAIToolDisabledTooltip, isAIToolScopeDisabled } from 'src/shared/aiToolScopeStatus';
import { useFeatureFlag } from 'src/shared/feature-flags';
import { T, toGlobalID } from 'src/shared/helpers';
import styled from 'styled-components';
import { QuestionDialog, useDialogManager } from '../../../../widgets/dialog';
import { AssetLibrary } from '../../assetLibrary/assetLibrary';
import { TOOLS } from '../../constants';
import { CustomAppFrontend } from '../../customApps/customAppFrontend';
import { CustomAppsManagerDialog } from '../../customApps/customAppsManagerDialog';
import { AI_STUDIO_CUSTOM_APP_SCOPE, AICustomApp } from '../../customApps/customAppTypes';
import { useAIStudioGeneration } from '../../hooks/useAIStudioGeneration';
import { getInitialSelectionsForTool, updateToolState, useAIStudioState } from '../../hooks/useAIStudioState';
import { useDefaultSelections } from '../../hooks/useDefaultSelections';
import { useIframeMessages } from '../../hooks/useIframeMessages';
import { useProductScene } from '../../hooks/useProductScene';
import { getModel3DParameterSelectionKey, Model3DParameterSelections, Model3DParameterValues } from '../../modelGeneration3DParameters';
import { aiCustomAppsQuery as aiCustomAppsQueryType } from '../../queries/__generated__/aiCustomAppsQuery.graphql';
import { modelGeneration3DModelsQuery } from '../../queries/__generated__/modelGeneration3DModelsQuery.graphql';
import { videoGenerationModelsQuery } from '../../queries/__generated__/videoGenerationModelsQuery.graphql';
import { aiCustomAppsQuery } from '../../queries/aiCustomApps';
import { ModelGeneration3DModelsQuery } from '../../queries/modelGeneration3DModels';
import { VideoGenerationModelsQuery } from '../../queries/videoGenerationModels';
import { InputImageData, MODEL3D_TOOL_IDS, Model3DModelSelections, ToolType } from '../../types';
import { fileToBase64, getSideImageAsBase64 } from '../../utils/imageHelpers';
import { useAIStudioProduct } from '../context/productContext';
import { useViewer3DCapture, Viewer3DProvider } from '../context/viewer3DContext';
import { useAIStudioCreditsDialog } from '../dialogs/aiStudioNoCreditsDialog';
import { AssetPickerDialog } from '../dialogs/assetPickerDialog';
import { getSupportedMultiViewSlotIds, MultiViewInputData, ViewSlotId } from '../inputs/multiViewDropZone';
import ProductCatalogDialog from '../dialogs/productCatalogDialog';
import { AIStudioHomepage, CREATIVE_STUDIO_TOOL_CARDS, MODEL3D_SUBCARDS } from '../navigation/aiStudioHomepage';
import { Header } from '../navigation/header';
import { getDefaultModelGeneration3DModel, isModelGeneration3DModelSupported } from '../navigation/model3DSettingsSidebar';
import { PresetDropdownValue } from '../presets/presetDropdown';
import { AutoCapture3D } from '../shared/autoCapture3D';
import { BackToProductButton } from '../shared/backToProductButton';
import ProductIframe from '../shared/productIframe';
import { aiStudioContentEnvironmentQuery } from './__generated__/aiStudioContentEnvironmentQuery.graphql';
import { aiStudioContentToolScopeStatusQuery } from './__generated__/aiStudioContentToolScopeStatusQuery.graphql';
import { BottomPanel } from './bottomPanel';
import { CanvasArea } from './canvasArea';
import { SettingsPanel } from './settingsPanel';

const Container = styled.div<{ $isContextual: boolean }>`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    background: ${(props) => (props.$isContextual ? props.theme.app.backgroundColor : 'transparent')};
    font-family: ${(props) => props.theme.app.font};
    position: relative;

    button,
    input,
    select,
    textarea {
        font-family: inherit;
    }
`;

const ContentArea = styled.div`
    flex: 1;
    display: flex;
    flex-direction: row;
    min-height: 0;
`;

const CenterColumn = styled.div<{ $isContextual: boolean }>`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    background: ${(props) => (props.$isContextual ? 'linear-gradient(160deg, #fdfcff 0%, #faf8ff 50%, #fffafc 100%)' : 'transparent')};
`;

const MainContent = styled.div`
    flex: 1;
    display: flex;
    min-height: 0;
`;

interface AIStudioContentProps {
    onClose?: () => void;
    initialTool?: ToolType;
    initialView?: 'home' | 'library';
    onLibraryToggle?: () => void;
    onGoHome?: () => void;
    autoCapture3D?: boolean;
}

const toolScopeStatusQuery = graphql`
    query aiStudioContentToolScopeStatusQuery {
        aiStudioImageScopeStatus: aiToolScopeStatus(scope: AI_STUDIO_IMAGE) {
            isDisabled
        }
        aiStudioVideoScopeStatus: aiToolScopeStatus(scope: AI_STUDIO_VIDEO) {
            isDisabled
        }
        aiStudioUpscalerScopeStatus: aiToolScopeStatus(scope: AI_STUDIO_UPSCALER) {
            isDisabled
        }
        aiStudio3DModelScopeStatus: aiToolScopeStatus(scope: AI_STUDIO_3D_MODEL) {
            isDisabled
        }
    }
`;

const ContextualCustomAppsLoader: FC = () => {
    useLazyLoadQuery<aiCustomAppsQueryType>(aiCustomAppsQuery, { scope: AI_STUDIO_CUSTOM_APP_SCOPE }, { fetchPolicy: 'store-or-network' });
    return null;
};

const ContextualToolScopesLoader: FC = () => {
    useLazyLoadQuery<aiStudioContentToolScopeStatusQuery>(toolScopeStatusQuery, {}, { fetchPolicy: 'store-or-network' });
    return null;
};

const ContextualVideoModelsLoader: FC = () => {
    useLazyLoadQuery<videoGenerationModelsQuery>(VideoGenerationModelsQuery, {}, { fetchPolicy: 'store-or-network' });
    return null;
};

const ContextualModel3DModelsLoader: FC = () => {
    useLazyLoadQuery<modelGeneration3DModelsQuery>(ModelGeneration3DModelsQuery, {}, { fetchPolicy: 'store-or-network' });
    return null;
};

/**
 * Detects 3D scene availability for a catalog-selected product.
 * Must only be rendered when productId is defined (avoids conditional hook issue in useProductScene).
 */
const CatalogProduct3DDetector: FC<{ productId: number }> = ({ productId }) => {
    const sceneHas3D = useProductScene(productId);
    const { updateProduct } = useAIStudioProduct();
    useEffect(() => {
        updateProduct({ has3D: sceneHas3D });
    }, [sceneHas3D, updateProduct]);
    return null;
};

/**
 * Fetches zakekeEnvironment from the product and pushes it into the product context.
 * Covers DAM (VIEWER) and customizer (CUSTOMIZER) paths where ProductIframe is not used.
 */
const ProductEnvironmentDetector: FC<{ productId: number }> = ({ productId }) => {
    const data = useLazyLoadQuery<aiStudioContentEnvironmentQuery>(
        graphql`
            query aiStudioContentEnvironmentQuery($productId: ID!) {
                model(id: $productId) {
                    zakekeEnvironment
                }
            }
        `,
        { productId: toGlobalID(productId, 'Model')! }
    );
    const { updateProduct } = useAIStudioProduct();
    const env = data.model?.zakekeEnvironment;
    useEffect(() => {
        if (env === 'CUSTOMIZER' || env === 'COMPOSER' || env === 'VIEWER') {
            updateProduct({ environment: env });
        }
    }, [env, updateProduct]);
    return null;
};

/** Inner component that uses Viewer3DCapture context */
const AIStudioContentInner: FC<AIStudioContentProps> = ({ onClose, initialTool, initialView, onLibraryToggle, onGoHome, autoCapture3D }) => {
    const { sideImages, has3D, productId, updateProduct, resetProduct, model3D, model3DRootUrl, environment } = useAIStudioProduct();
    const viewer3DCapture = useViewer3DCapture();
    const { showDialog, closeDialog } = useDialogManager();
    const isAiStudio3DEnabled = useFeatureFlag('aiStudio3D');
    const isMadooEnabled = useFeatureFlag('madoo');
    const visibleTools = useMemo(() => TOOLS.filter((t) => !MODEL3D_TOOL_IDS.includes(t.id as ToolType) || isAiStudio3DEnabled), [isAiStudio3DEnabled]);
    const [customAppsFetchKey, setCustomAppsFetchKey] = React.useState(0);
    const [selectedCustomApp, setSelectedCustomApp] = React.useState<AICustomApp | null>(null);
    const customAppsData = useLazyLoadQuery<aiCustomAppsQueryType>(
        aiCustomAppsQuery,
        { scope: AI_STUDIO_CUSTOM_APP_SCOPE },
        { fetchPolicy: onClose ? 'store-only' : 'store-and-network', fetchKey: customAppsFetchKey }
    );
    const customApps = useMemo(
        () =>
            (customAppsData?.aiCustomApps ?? [])
                .filter((app): app is NonNullable<typeof app> => app !== null)
                .map((app) => ({
                    aiCustomAppID: app.aiCustomAppID,
                    name: app.name,
                    description: app.description,
                    mediaFactoryAppID: app.mediaFactoryAppID,
                    isActive: app.isActive
                })),
        [customAppsData?.aiCustomApps]
    );

    const activeCustomApps = useMemo(() => customApps.filter((app) => app.isActive), [customApps]);
    const scopeStatusData = useLazyLoadQuery<aiStudioContentToolScopeStatusQuery>(
        toolScopeStatusQuery,
        {},
        { fetchPolicy: onClose ? 'store-only' : 'store-and-network' }
    );
    const disabledTooltipByScopeKey = useMemo(
        () => ({
            image: isAIToolScopeDisabled(scopeStatusData?.aiStudioImageScopeStatus) ? getAIToolDisabledTooltip() : undefined,
            video: isAIToolScopeDisabled(scopeStatusData?.aiStudioVideoScopeStatus) ? getAIToolDisabledTooltip() : undefined,
            upscaler: isAIToolScopeDisabled(scopeStatusData?.aiStudioUpscalerScopeStatus) ? getAIToolDisabledTooltip() : undefined,
            model3d: isAIToolScopeDisabled(scopeStatusData?.aiStudio3DModelScopeStatus) ? getAIToolDisabledTooltip() : undefined
        }),
        [scopeStatusData]
    );
    const getDisabledTooltipForTool = useCallback(
        (toolId: string) => {
            const scopeKey = getAIStudioToolScopeKey(toolId);
            return scopeKey ? disabledTooltipByScopeKey[scopeKey] : undefined;
        },
        [disabledTooltipByScopeKey]
    );
    const disabledTooltipByToolId = useMemo(() => {
        const toolIds = new Set<string>([...TOOLS.map((tool) => tool.id), ...MODEL3D_SUBCARDS.map((card) => card.id)]);
        const disabledTooltips: Record<string, string | undefined> = {};
        toolIds.forEach((toolId) => {
            disabledTooltips[toolId] = getDisabledTooltipForTool(toolId);
        });
        return disabledTooltips;
    }, [getDisabledTooltipForTool]);

    // --- Video generation models (needed for initial video options) ---
    const modelsData = useLazyLoadQuery<videoGenerationModelsQuery>(
        VideoGenerationModelsQuery,
        {},
        { fetchPolicy: onClose ? 'store-only' : 'store-or-network' }
    );
    const model3DModelsData = useLazyLoadQuery<modelGeneration3DModelsQuery>(
        ModelGeneration3DModelsQuery,
        {},
        { fetchPolicy: onClose ? 'store-only' : 'store-or-network' }
    );
    const modelGeneration3DModels = model3DModelsData?.modelGeneration3DModels ?? [];
    const defaultVideoGenerationModel = React.useMemo(
        () => modelsData?.videoGenerationModels?.find((model) => model.isDefault) ?? modelsData?.videoGenerationModels?.[0],
        [modelsData?.videoGenerationModels]
    );
    const [model3DModelSelections, setModel3DModelSelections] = React.useState<Model3DModelSelections>({});
    const [model3DParameterSelections, setModel3DParameterSelections] = React.useState<Model3DParameterSelections>({});
    const getSelectedModel3DModel = React.useCallback(
        (agentId: string | null | undefined) => {
            if (!agentId) return undefined;
            const selectedModel = model3DModelSelections[agentId as ToolType];
            if (isModelGeneration3DModelSupported(modelGeneration3DModels, agentId, selectedModel)) {
                return selectedModel;
            }
            return getDefaultModelGeneration3DModel(modelGeneration3DModels, agentId);
        },
        [model3DModelSelections, modelGeneration3DModels]
    );
    const handleModel3DModelChange = React.useCallback((agentId: string, model: string) => {
        setModel3DModelSelections((prev) => ({ ...prev, [agentId as ToolType]: model }));
    }, []);
    const getSelectedModel3DParameterValues = React.useCallback(
        (agentId: string | null | undefined): Model3DParameterValues | undefined => {
            const model = getSelectedModel3DModel(agentId);
            if (!agentId || !model) return undefined;
            return model3DParameterSelections[getModel3DParameterSelectionKey(agentId, model)];
        },
        [getSelectedModel3DModel, model3DParameterSelections]
    );
    const handleModel3DParameterValuesChange = React.useCallback((agentId: string, model: string, values: Model3DParameterValues) => {
        setModel3DParameterSelections((prev) => ({
            ...prev,
            [getModel3DParameterSelectionKey(agentId, model)]: values
        }));
    }, []);
    const [multiViewInputs, setMultiViewInputs] = React.useState<MultiViewInputData[]>([]);

    // --- Core state hook ---
    const {
        state,
        setState,
        results,
        selectedImageIndex,
        setSelectedImageIndex,
        inputImages,
        setInputImages,
        isIframeMode,
        setIsIframeMode,
        isFromCatalog,
        setIsFromCatalog,
        viewMode,
        setViewMode,
        currentToolView,
        generationStartedAt,
        globalView,
        currentToolConfig,
        hasProductInput,
        productImageUrl,
        selectionState,
        goToHome,
        goToEditor,
        goToGallery,
        goToLibrary,
        goBack,
        handleToolChange,
        handlePromptChange,
        handlePresetSelect,
        handleDeleteResult,
        handleReturnToCustomizer,
        isGenerating,
        isProductInputEditing,
        setIsProductInputEditing,
        isAutoCapturing,
        setIsAutoCapturing,
        videoOptions,
        setVideoOptions,
        adOptions,
        setAdOptions,
        imageAspectRatios,
        handleImageAspectRatioChange
    } = useAIStudioState({
        initialTool,
        initialView,
        sideImages,
        has3D,
        productId,
        initialVideoModel: defaultVideoGenerationModel,
        autoCapture3D
    });

    useEffect(() => {
        if (initialView) {
            setState((previous) =>
                previous.globalView === initialView || (initialView === 'home' && previous.globalView === 'tool')
                    ? previous
                    : { ...previous, globalView: initialView }
            );
        }
    }, [initialView, setState]);
    const selectedModel3DInfo = useMemo(() => {
        const agentId = state.selectedTool;
        const model = getSelectedModel3DModel(agentId);
        return modelGeneration3DModels.find((item) => item.model === model && item.supportedModes.includes(agentId ?? ''));
    }, [getSelectedModel3DModel, modelGeneration3DModels, state.selectedTool]);
    const supportedMultiViewSlotIds = useMemo(() => getSupportedMultiViewSlotIds(selectedModel3DInfo), [selectedModel3DInfo]);

    useEffect(() => {
        if (state.selectedTool !== 'multi-views-to-3d') return;
        const supportedSlots = new Set<ViewSlotId>(supportedMultiViewSlotIds);
        setMultiViewInputs((prev) => {
            const next = prev.filter((input) => supportedSlots.has(input.viewType));
            return next.length === prev.length ? prev : next;
        });
    }, [state.selectedTool, supportedMultiViewSlotIds]);

    // --- Generation hook ---
    const isInTrialOrTestStore = false;
    const pendingGenerateRef = useRef(false);
    const handleGenerateRef = useRef<() => Promise<void>>(async () => {});

    const { showNoCreditsDialog } = useAIStudioCreditsDialog();

    const { handleGenerate, handleStopGeneration, canGenerate } = useAIStudioGeneration({
        state,
        setState,
        inputImages,
        setInputImages,
        multiViewInputs,
        sideImages,
        selectedImageIndex,
        viewMode,
        has3D,
        productId,
        viewer3DCapture,
        videoOptions,
        adOptions,
        imageAspectRatios,
        model3DModel: getSelectedModel3DModel(state.selectedTool),
        modelGeneration3DModels,
        model3DParameterValues: getSelectedModel3DParameterValues(state.selectedTool),
        productImageUrl,
        isInTrialOrTestStore,
        isAutoCapturing,
        onNoCredits: () => showNoCreditsDialog(T._('Not enough credits to execute the operation', 'Admin')),
        setIsProductInputEditing,
        isProductInputEditing,
        hasProductInput
    });

    // Keep handleGenerateRef up-to-date so the pending-generate effect always calls the latest version
    handleGenerateRef.current = handleGenerate;

    // Auto-trigger generation after navigating from the landing view
    useEffect(() => {
        if (pendingGenerateRef.current && state.globalView === 'tool' && state.selectedTool !== null) {
            pendingGenerateRef.current = false;
            handleGenerateRef.current();
        }
    }, [state.globalView, state.selectedTool]);

    // --- Homepage overlay state (lifted so Header can show tabs) ---
    const [homepageOverlayToolId, setHomepageOverlayToolId] = React.useState<ToolType | null>(null);
    const [homepageOverlayAgentId, setHomepageOverlayAgentId] = React.useState<string | null>(null);

    // Image-to-3D with a product loaded skips the landing so the shared workspace can expose
    // the 2D/3D product input editor before generation.
    const landingTabs = useMemo(() => {
        if (!homepageOverlayToolId && !homepageOverlayAgentId) return undefined;
        return [
            ...CREATIVE_STUDIO_TOOL_CARDS.filter((card) => visibleTools.some((tool) => tool.id === card.id)).map((card) => ({
                id: card.id,
                name: card.name,
                active: card.id === homepageOverlayToolId,
                disabledTooltip: getDisabledTooltipForTool(card.id),
                onClick: () => {
                    if (getDisabledTooltipForTool(card.id)) return;
                    // When a product is loaded, Creative Studio tools skip their landing and go
                    // straight to the workspace — same behavior as the homepage card click and
                    // the header toolbar click outside of overlay mode. Without this, clicking
                    // a Creative Studio tab from inside a 3D agent landing (or another tool's
                    // landing) would just swap the landing overlay and leave the user on a blank
                    // upload zone instead of the workspace with the product image already loaded.
                    if (productId) {
                        setHomepageOverlayAgentId(null);
                        setHomepageOverlayToolId(null);
                        handleToolChange(card.id);
                        return;
                    }
                    setHomepageOverlayAgentId(null);
                    setHomepageOverlayToolId(card.id);
                }
            })),
            ...(isAiStudio3DEnabled
                ? MODEL3D_SUBCARDS.map((card) => ({
                      id: card.id,
                      name: card.name,
                      active: card.id === homepageOverlayAgentId,
                      disabledTooltip: getDisabledTooltipForTool(card.id),
                      onClick: () => {
                          if (getDisabledTooltipForTool(card.id)) return;
                          // image-to-3d in product mode skips its landing and goes straight to the
                          // Creative Studio workspace (ProductInputEditor + Images/3D View toggle).
                          if (card.id === 'image-to-3d' && productId) {
                              setHomepageOverlayAgentId(null);
                              setHomepageOverlayToolId(null);
                              handleToolChange(card.id);
                              return;
                          }
                          setHomepageOverlayToolId(null);
                          setHomepageOverlayAgentId(card.id);
                      }
                  }))
                : [])
        ];
    }, [homepageOverlayToolId, homepageOverlayAgentId, isAiStudio3DEnabled, visibleTools, getDisabledTooltipForTool, productId, handleToolChange]);

    // Per-tool snapshot of the user-uploaded / library-picked input images. The shared
    // `inputImages` state always reflects the currently selected tool; on a header tab switch
    // we save the source tool's images here and load the destination tool's images back, so
    // each 2D tool keeps its own inputs and they don't leak across tools.
    const [inputImagesByTool, setInputImagesByTool] = React.useState<Partial<Record<ToolType, InputImageData[]>>>({});

    // Header tool-tab handler: when switching tool from the workspace, show the
    // destination tool's empty state (homepage landing overlay) instead of jumping
    // straight into the workspace. This ensures the user always sees a clean
    // empty state with the correct prompt for the target tool.
    const handleHeaderToolChange = React.useCallback(
        (toolId: ToolType) => {
            if (getDisabledTooltipForTool(toolId)) return;
            setSelectedCustomApp(null);

            // 3D agents with a product loaded show their landing with the product image
            // pre-filled before entering the shared workspace.
            // Exception: image-to-3d in product mode uses the standard Creative Studio
            // workspace (with ProductInputEditor + Images/3D View toggle) so the user can
            // rotate the 3D scene to pick a different angle before sending — same pattern as
            // upscaler/product-transformer. Falling through to the regular workspace path
            // below handles this.
            if (productId && MODEL3D_TOOL_IDS.includes(toolId) && toolId !== 'image-to-3d') {
                setState((prev) => ({
                    ...prev,
                    selectedTool: null,
                    globalView: 'home' as const,
                    prompt: '',
                    selections: {}
                }));
                setHomepageOverlayToolId(null);
                setHomepageOverlayAgentId(toolId);
                return;
            }

            // Preserve in-progress generations and existing results across tool switches.
            // All tools, including the 3D agents, store this data in toolStates.
            const hasAnySavedState = Object.values(state.toolStates).some((ts) => !!ts && (ts.isGenerating || ts.results.length > 0));
            if (productId || hasAnySavedState) {
                if (MODEL3D_TOOL_IDS.includes(toolId)) {
                    setMultiViewInputs((prev) => (toolId === 'multi-views-to-3d' ? prev : []));
                } else {
                    // Heading into a non-3D tool view — clear any leftover landing-overlay
                    // state from a previous 3D-agent toolbar click so it doesn't reappear
                    // the next time the user navigates back to home.
                    setHomepageOverlayAgentId(null);
                    setHomepageOverlayToolId(null);
                }
                if (productId) {
                    handleToolChange(toolId);
                } else {
                    // Save the source tool's inputImages and load the destination tool's
                    // saved inputImages (empty for first visit). This isolates per-tool inputs
                    // so an image uploaded in one tool doesn't leak into another. Done only
                    // outside product mode — when a product is loaded its image is shared.
                    // We treat the presence of sideImages as a product-context hint as well,
                    // so testPage/preview flows still share the side image across tools.
                    const hasProductContext = !!sideImages && sideImages.length > 0;
                    if (hasProductContext) {
                        // Product context: keep inputImages shared across tools.
                    } else {
                        const sourceTool = state.selectedTool;
                        const destinationImages = sourceTool === toolId ? inputImages : (inputImagesByTool[toolId] ?? []);
                        if (sourceTool && sourceTool !== toolId) {
                            setInputImagesByTool((prev) => ({ ...prev, [sourceTool]: inputImages }));
                        }
                        setInputImages(destinationImages);
                    }
                    setState((prev) => ({
                        ...prev,
                        selectedTool: toolId,
                        prompt: '',
                        selections: {},
                        globalView: 'tool' as const,
                        error: null
                    }));
                    setIsProductInputEditing(false);
                }
                return;
            }

            goToHome();

            if (MODEL3D_TOOL_IDS.includes(toolId)) {
                setHomepageOverlayToolId(null);
                setHomepageOverlayAgentId(toolId);
            } else {
                setHomepageOverlayAgentId(null);
                setHomepageOverlayToolId(toolId);
            }
        },
        [
            goToHome,
            productId,
            handleToolChange,
            state.toolStates,
            state.selectedTool,
            sideImages,
            inputImages,
            inputImagesByTool,
            setInputImages,
            setState,
            setIsProductInputEditing,
            getDisabledTooltipForTool,
            setMultiViewInputs
        ]
    );

    const handleCustomAppChange = React.useCallback(
        (app: AICustomApp) => {
            if (isAutoCapturing) return;
            setHomepageOverlayToolId(null);
            setHomepageOverlayAgentId(null);
            setSelectedCustomApp(app);
            setIsIframeMode(false);
            setState((prev) => ({
                ...prev,
                selectedTool: null,
                prompt: '',
                selections: {},
                error: null
            }));
        },
        [isAutoCapturing, setIsIframeMode, setState]
    );

    const handleCustomAppsChanged = React.useCallback(() => {
        setCustomAppsFetchKey((prev) => prev + 1);
    }, []);

    const handleCustomAppDeleted = React.useCallback(
        (app: AICustomApp) => {
            if (selectedCustomApp?.aiCustomAppID !== app.aiCustomAppID) return;

            setSelectedCustomApp(null);
            goToHome();
        },
        [goToHome, selectedCustomApp?.aiCustomAppID]
    );

    const handleManageCustomApps = React.useCallback(() => {
        showDialog(
            'ai-studio-custom-apps-manager',
            <CustomAppsManagerDialog
                onChanged={handleCustomAppsChanged}
                onDeleted={handleCustomAppDeleted}
                onClose={() => closeDialog('ai-studio-custom-apps-manager')}
            />
        );
    }, [closeDialog, handleCustomAppDeleted, handleCustomAppsChanged, showDialog]);

    const handleModel3DAgentSelect = React.useCallback(
        async (
            agentId: string,
            prompt?: string,
            imageFile?: File,
            _multiViewReady?: boolean,
            imageUrl?: string,
            mvFiles?: Array<{ file: File; viewType: string }>
        ) => {
            if (getDisabledTooltipForTool(agentId)) return;

            const toolId = agentId as ToolType;
            if (toolId === 'image-to-3d') {
                const url = imageFile ? await fileToBase64(imageFile) : imageUrl;
                setInputImages(url ? [{ id: `input-3d-${Date.now()}`, url, name: imageFile?.name ?? 'Library image' }] : []);
            } else {
                setInputImages([]);
            }

            if (toolId === 'multi-views-to-3d') {
                const model = getSelectedModel3DModel(toolId);
                const modelInfo = modelGeneration3DModels.find((item) => item.model === model && item.supportedModes.includes(toolId));
                const supportedSlots = new Set<string>(getSupportedMultiViewSlotIds(modelInfo));
                setMultiViewInputs(
                    (mvFiles ?? [])
                        .filter((input): input is { file: File; viewType: ViewSlotId } => supportedSlots.has(input.viewType))
                        .map((input) => ({ file: input.file, viewType: input.viewType }))
                );
            } else {
                setMultiViewInputs([]);
            }

            setState((prev) => ({
                ...updateToolState(prev, toolId, { view: 'gallery' }),
                selectedTool: toolId,
                prompt: prompt ?? '',
                selections: getInitialSelectionsForTool(toolId),
                globalView: 'tool',
                error: null
            }));
            setIsProductInputEditing(false);
            pendingGenerateRef.current = true;
        },
        [getDisabledTooltipForTool, getSelectedModel3DModel, modelGeneration3DModels, setInputImages, setIsProductInputEditing, setState]
    );

    // --- Generate from landing view ---
    const handleGenerateFromLanding = useCallback(
        (toolId: ToolType, images: InputImageData[], prompt: string, selections: Record<string, PresetDropdownValue | null>) => {
            if (getDisabledTooltipForTool(toolId)) return;

            if (images.length > 0) {
                setInputImages(images);
            }
            // Merge tool defaults under the landing's user-picked selections so canGenerate
            // sees mandatory presets (e.g. virtual-tryon's Model) on the first render and the
            // pending-generate effect doesn't race the async useDefaultSelections fetch.
            const mergedSelections = { ...getInitialSelectionsForTool(toolId), ...selections };
            setState((prev) => ({
                ...updateToolState(prev, toolId, { view: 'gallery' }),
                selectedTool: toolId,
                prompt,
                selections: mergedSelections,
                globalView: 'tool',
                error: null
            }));
            setIsProductInputEditing(false);
            pendingGenerateRef.current = true;
        },
        [setInputImages, setState, setIsProductInputEditing, getDisabledTooltipForTool]
    );

    const handleOpenLibraryForLanding = useCallback(
        (onSelect: (imageUrl: string) => void) => {
            showDialog(
                'asset-picker-dialog-landing',
                <Suspense fallback={null}>
                    <AssetPickerDialog
                        onSelect={(imageUrl) => {
                            closeDialog('asset-picker-dialog-landing');
                            onSelect(imageUrl);
                        }}
                    />
                </Suspense>
            );
        },
        [showDialog, closeDialog]
    );

    // --- Default selections ---
    useDefaultSelections(state.selectedTool, setState);

    // --- Iframe messages ---
    useIframeMessages({
        updateProduct,
        setIsIframeMode,
        setState,
        setSelectedImageIndex,
        setViewMode,
        setIsProductInputEditing,
        setInputImages,
        setIsAutoCapturing
    });

    // Ensure the product image stays in inputImages whenever a product is open. Runs on mount
    // (initial side-image load for testPage 2D) AND re-runs if inputImages loses its fromProduct
    // entry mid-flow — for example, when the user navigates between a 3D agent and a Creative
    // Studio tool via the header toolbar and some transient state change drops the product
    // image. For 3D products without sideImages, we re-arm AutoCapture3D by flipping
    // isAutoCapturing back on; the AutoCapture3D component will then re-render its hidden
    // scene viewer and replace the missing entry once the screenshot is ready.
    // Note: clearProductState (the user's explicit "remove product" action) sets productId to
    // undefined, so the early return below keeps this effect from undoing an intentional removal.
    useEffect(() => {
        if (!productId) return;
        if (inputImages.some((img) => img.fromProduct)) return;
        if (has3D) {
            if (!isAutoCapturing) setIsAutoCapturing(true);
            return;
        }
        if (!sideImages || sideImages.length === 0) return;
        const selectedSide = sideImages[0];
        let cancelled = false;
        getSideImageAsBase64(selectedSide).then((base64) => {
            if (cancelled || !base64) return;
            setInputImages((prev) => {
                if (prev.some((img) => img.fromProduct)) return prev;
                return [
                    ...prev.filter((img) => !img.fromProduct),
                    {
                        id: `input-side-${Date.now()}`,
                        url: base64,
                        name: selectedSide.sideName || 'Product image',
                        fromProduct: true
                    }
                ];
            });
        });
        return () => {
            cancelled = true;
        };
    }, [productId, has3D, sideImages, inputImages, isAutoCapturing, setInputImages, setIsAutoCapturing]);

    // --- Document title ---
    useEffect(() => {
        const previousTitle = document.title;
        document.title = 'Zakeke - AI Studio';
        return () => {
            document.title = previousTitle;
        };
    }, []);

    // --- Dialog callbacks (need showDialog/closeDialog JSX) ---
    const handleUseAsInput = useCallback(
        (imageUrl: string) => {
            const applyAsInput = () => {
                setInputImages([{ id: `input-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`, url: imageUrl, name: 'Input image' }]);
                goToEditor();
                // When invoked from the asset library, navigate back to the tool view
                setState((prev) => (prev.globalView === 'library' ? { ...prev, globalView: 'tool' } : prev));
            };

            const isInProductMode = (sideImages && sideImages.length > 0) || (has3D && productId);

            if (isInProductMode) {
                showDialog(
                    'use-as-input-confirm',
                    <QuestionDialog
                        message={
                            <span>
                                {T._(
                                    'Using this image will replace the current input and exit from the product catalog mode. Do you want to continue?',
                                    'Admin'
                                )}
                            </span>
                        }
                        onYesClick={() => {
                            closeDialog('use-as-input-confirm');
                            applyAsInput();
                        }}
                    />
                );
            } else {
                applyAsInput();
            }
        },
        [sideImages, has3D, productId, showDialog, closeDialog, goToEditor, setState, setInputImages]
    );

    const handleOpenCatalog = useCallback(() => {
        showDialog(
            'product-catalog-dialog',
            <Suspense fallback={null}>
                <ProductCatalogDialog
                    onConfirm={(modelID) => {
                        const applyProduct = () => {
                            closeDialog('product-catalog-dialog');
                            updateProduct({ productId: modelID });
                            setIsIframeMode(true);
                            setIsFromCatalog(true);
                            setViewMode('images');
                        };

                        if (productId) {
                            closeDialog('product-catalog-dialog');
                            showDialog(
                                'switch-product-confirm',
                                <QuestionDialog
                                    message={<span>{T._('All work will be lost. Are you sure you want to switch product?', 'Admin')}</span>}
                                    onYesClick={() => {
                                        closeDialog('switch-product-confirm');
                                        applyProduct();
                                    }}
                                />
                            );
                        } else {
                            applyProduct();
                        }
                    }}
                />
            </Suspense>
        );
    }, [showDialog, closeDialog, updateProduct, productId, setIsIframeMode, setIsFromCatalog, setViewMode]);

    const handleOpenLibraryDialog = useCallback(() => {
        const supportsMultiple = state.selectedTool === 'multi-image-fusion';
        showDialog(
            'asset-picker-dialog',
            <Suspense fallback={null}>
                <AssetPickerDialog
                    onSelect={(imageUrl) => {
                        closeDialog('asset-picker-dialog');
                        const newImage: InputImageData = {
                            id: `input-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                            url: imageUrl,
                            name: 'Library asset'
                        };
                        if (supportsMultiple) {
                            setInputImages((prev) => [...prev, newImage]);
                        } else {
                            setInputImages([newImage]);
                        }
                    }}
                />
            </Suspense>
        );
    }, [showDialog, closeDialog, state.selectedTool, setInputImages]);

    // --- Pending catalog open after tool navigation from homepage ---
    const [pendingCatalogOpen, setPendingCatalogOpen] = React.useState(false);

    React.useEffect(() => {
        if (pendingCatalogOpen && globalView === 'tool') {
            handleOpenCatalog();
            setPendingCatalogOpen(false);
        }
    }, [globalView, pendingCatalogOpen, handleOpenCatalog]);

    const handleOpenCatalogFromHome = React.useCallback(
        (toolId: ToolType) => {
            if (getDisabledTooltipForTool(toolId)) return;

            setPendingCatalogOpen(true);
            handleToolChange(toolId);
        },
        [handleToolChange, getDisabledTooltipForTool]
    );

    /** Resets product state: clears input images, exits iframe/catalog mode, resets product context */
    const clearProductState = useCallback(() => {
        setIsIframeMode(false);
        setInputImages([]);
        setViewMode('images');
        setIsFromCatalog(false);
        updateProduct({
            sideImages: [],
            has3D: false,
            productId: undefined,
            variantId: undefined,
            compositionDocId: undefined,
            docId: undefined
        });
        resetProduct();
    }, [setIsIframeMode, setInputImages, setViewMode, setIsFromCatalog, updateProduct, resetProduct]);

    const handleBackToTool = useCallback(() => {
        showDialog(
            'back-to-tool-confirm',
            <QuestionDialog
                message={<span>{T._('Any work will be lost. Are you sure you want to go back?', 'Admin')}</span>}
                onYesClick={() => {
                    closeDialog('back-to-tool-confirm');
                    clearProductState();
                }}
            />
        );
    }, [showDialog, closeDialog, clearProductState]);

    const handleRemoveProduct = useCallback(() => {
        showDialog(
            'remove-product-confirm',
            <QuestionDialog
                message={
                    <span>
                        {T._(
                            'This will remove the product input and exit the product mode. The customization or configuration will be lost. Are you sure?',
                            'Admin'
                        )}
                    </span>
                }
                onYesClick={() => {
                    closeDialog('remove-product-confirm');
                    clearProductState();
                }}
            />
        );
    }, [showDialog, closeDialog, clearProductState]);

    // --- Auto-capture 3D handler ---
    const handleAutoCapture3D = useCallback(
        (base64: string) => {
            setInputImages((prev) => [
                ...prev.filter((img) => !img.fromProduct),
                { id: `input-3d-${Date.now()}`, url: base64, name: '3D Capture', fromProduct: true }
            ]);
            setIsAutoCapturing(false);
        },
        [setInputImages, setIsAutoCapturing]
    );

    const handleLibraryToggle = React.useCallback(() => {
        if (globalView === 'library') {
            goBack();
        } else {
            setSelectedCustomApp(null);
            goToLibrary();
        }
        onLibraryToggle?.();
    }, [globalView, goBack, goToLibrary, onLibraryToggle]);

    return (
        <Container $isContextual={!!onClose}>
            {onClose && (
                <>
                    <Suspense fallback={null}>
                        <ContextualCustomAppsLoader />
                    </Suspense>
                    <Suspense fallback={null}>
                        <ContextualToolScopesLoader />
                    </Suspense>
                    <Suspense fallback={null}>
                        <ContextualVideoModelsLoader />
                    </Suspense>
                    <Suspense fallback={null}>
                        <ContextualModel3DModelsLoader />
                    </Suspense>
                </>
            )}
            {/* Show back button only when not in library view and not in iframe mode */}
            {globalView !== 'library' && !isIframeMode && productId && onClose && (
                <BackToProductButton sideImages={sideImages} onClose={isFromCatalog ? handleReturnToCustomizer : onClose} environment={environment} />
            )}

            {/* Hidden 3D auto-capture when returning from iframe with 3D-only product */}
            {isAutoCapturing && productId && (
                <Suspense fallback={null}>
                    <AutoCapture3D productId={productId} onCaptured={handleAutoCapture3D} model3D={model3D} model3DRootUrl={model3DRootUrl} />
                </Suspense>
            )}

            {/* The standalone Library uses the shared gallery page header instead of the Studio toolbar. */}
            {(globalView !== 'library' || !!onClose) && (
                <Header
                    tools={visibleTools}
                    customApps={activeCustomApps}
                    selectedTool={globalView === 'home' ? null : state.selectedTool}
                    selectedCustomAppId={selectedCustomApp?.aiCustomAppID}
                    onToolChange={handleHeaderToolChange}
                    onCustomAppChange={handleCustomAppChange}
                    onManageCustomApps={isMadooEnabled ? handleManageCustomApps : undefined}
                    onClose={isIframeMode ? undefined : onClose}
                    onGoHome={() => {
                        setSelectedCustomApp(null);
                        setHomepageOverlayToolId(null);
                        setHomepageOverlayAgentId(null);
                        setInputImagesByTool({});
                        setMultiViewInputs([]);
                        goToHome();
                        onGoHome?.();
                    }}
                    onShowLibrary={handleLibraryToggle}
                    isLibraryActive={globalView === 'library'}
                    isIframeMode={isIframeMode}
                    isContextual={!!onClose}
                    onSwitchProduct={handleOpenCatalog}
                    isNavigationDisabled={isAutoCapturing}
                    landingTabs={globalView === 'home' ? landingTabs : undefined}
                    disabledTooltipByToolId={disabledTooltipByToolId}
                />
            )}

            {/* Product iframe — only mounted when product was opened from the catalog inside AI Studio */}
            {isFromCatalog && productId && (
                <Suspense fallback={null}>
                    <ProductIframe productId={productId} visible={isIframeMode} onClose={handleBackToTool} />
                </Suspense>
            )}

            {/* If in iframe mode, override anything as we need to show the iframe. */}
            {!isIframeMode && (
                <>
                    {/* Library view — backend-driven: 3D models saved via saveToLibrary appear here. */}
                    {globalView === 'library' && (
                        <Suspense fallback={null}>
                            <AssetLibrary onUseAsInput={handleUseAsInput} standalone={!onClose} />
                        </Suspense>
                    )}

                    {/* Homepage */}
                    {selectedCustomApp && globalView !== 'library' && <CustomAppFrontend app={selectedCustomApp} />}

                    {globalView === 'home' && !selectedCustomApp && (
                        <AIStudioHomepage
                            isContextual={!!onClose}
                            onToolSelect={handleToolChange}
                            onModel3DAgentSelect={handleModel3DAgentSelect}
                            onOpenCatalog={handleOpenCatalogFromHome}
                            onGenerateFromLanding={handleGenerateFromLanding}
                            onSelectFromLibraryForLanding={handleOpenLibraryForLanding}
                            modelGeneration3DModels={modelGeneration3DModels}
                            model3DModelSelections={model3DModelSelections}
                            onModel3DModelChange={handleModel3DModelChange}
                            getModel3DParameterValues={getSelectedModel3DParameterValues}
                            onModel3DParameterValuesChange={handleModel3DParameterValuesChange}
                            overlayToolId={homepageOverlayToolId}
                            onOverlayToolChange={(toolId) => {
                                if (toolId !== null && getDisabledTooltipForTool(toolId)) return;

                                // If a product is already loaded (opened from testPage/DAM/catalog),
                                // skip the empty-state landing and go directly to the tool workspace
                                // so the product input is shown instead of blank upload zones.
                                if (toolId !== null && productId) {
                                    handleToolChange(toolId);
                                } else {
                                    setHomepageOverlayToolId(toolId);
                                }
                            }}
                            overlayAgentId={homepageOverlayAgentId}
                            onOverlayAgentChange={(agentId) => {
                                if (agentId !== null && getDisabledTooltipForTool(agentId)) return;

                                // image-to-3d in product mode goes straight to the Creative Studio workspace
                                // (ProductInputEditor with Images/3D View toggle) so the user can rotate the
                                // scene and pick an angle, exactly like upscaler/product-transformer.
                                if (agentId !== null && agentId === 'image-to-3d' && productId) {
                                    setHomepageOverlayToolId(null);
                                    setHomepageOverlayAgentId(null);
                                    handleToolChange(agentId as ToolType);
                                    return;
                                }

                                // Other 3D agents (text-to-3d, multi-views-to-3d) always show their
                                // empty-state landing — the product image is propagated into the landing's
                                // pre-filled inputs via Model3DLandingView (e.g. front view for multi-views-to-3d).
                                setHomepageOverlayAgentId(agentId);
                            }}
                            videoOptions={videoOptions}
                            onVideoOptionsChange={setVideoOptions}
                            adOptions={adOptions}
                            onAdOptionsChange={setAdOptions}
                            imageAspectRatios={imageAspectRatios}
                            onImageAspectRatioChange={handleImageAspectRatioChange}
                            disabledTooltipByToolId={disabledTooltipByToolId}
                            productImageUrl={inputImages.find((img) => img.fromProduct)?.url ?? productImageUrl}
                        />
                    )}

                    {/* Shared workspace for image, video and 3D generation tools. */}
                    {globalView === 'tool' && (
                        <ContentArea>
                            {/* Detect 3D scene availability for catalog-selected products */}
                            {productId && (
                                <Suspense fallback={null}>
                                    <CatalogProduct3DDetector productId={productId} />
                                </Suspense>
                            )}
                            {/* Detect environment from product (DAM/customizer paths — ProductIframe handles catalog) */}
                            {productId && !isFromCatalog && (
                                <Suspense fallback={null}>
                                    <ProductEnvironmentDetector productId={productId} />
                                </Suspense>
                            )}
                            <CenterColumn $isContextual={!!onClose}>
                                <MainContent>
                                    <CanvasArea
                                        view={currentToolView}
                                        results={results}
                                        isGenerating={isGenerating}
                                        generationStartedAt={generationStartedAt}
                                        onGoToEditor={goToEditor}
                                        onGoToGallery={goToGallery}
                                        onDelete={handleDeleteResult}
                                        onUseAsInput={handleUseAsInput}
                                        sourceToolID={state.selectedTool!}
                                        productImages={hasProductInput ? sideImages : undefined}
                                        selectedImageIndex={selectedImageIndex}
                                        has3D={hasProductInput && has3D}
                                        productId={hasProductInput ? productId : undefined}
                                        model3DRootUrl={model3DRootUrl}
                                        model3D={model3D}
                                        viewMode={viewMode}
                                        onViewModeChange={setViewMode}
                                        inputImages={inputImages}
                                        onClearInputImages={() => setInputImages([])}
                                        isProductInputEditing={isProductInputEditing}
                                    />
                                </MainContent>
                                <BottomPanel
                                    toolId={state.selectedTool!}
                                    prompt={state.prompt}
                                    onPromptChange={handlePromptChange}
                                    selectionState={selectionState}
                                    onPresetSelect={handlePresetSelect}
                                    onGenerate={handleGenerate}
                                    onStop={handleStopGeneration}
                                    isGenerating={isGenerating}
                                    canGenerate={canGenerate}
                                    inputPresetType={currentToolConfig?.inputPresetType}
                                    inputImages={inputImages}
                                    onInputImagesChange={setInputImages}
                                    multiViewInputs={multiViewInputs}
                                    onMultiViewInputsChange={setMultiViewInputs}
                                    supportedMultiViewSlotIds={supportedMultiViewSlotIds}
                                    isProductMode={hasProductInput}
                                    guideLink={currentToolConfig?.guideLink}
                                    promptPlaceholder={currentToolConfig?.promptPlaceholder}
                                    onOpenCatalog={handleOpenCatalog}
                                    onOpenLibrary={handleOpenLibraryDialog}
                                    onToggleInputEditor={() => {
                                        setIsProductInputEditing((prev) => {
                                            if (!prev) goToEditor();
                                            return !prev;
                                        });
                                    }}
                                    isAutoCapturing={isAutoCapturing}
                                    isProductInputEditing={isProductInputEditing}
                                    onRemoveProduct={hasProductInput ? handleRemoveProduct : undefined}
                                />
                            </CenterColumn>
                            <Suspense fallback={null}>
                                <SettingsPanel
                                    toolId={state.selectedTool!}
                                    videoOptions={videoOptions}
                                    onVideoOptionsChange={setVideoOptions}
                                    adOptions={adOptions}
                                    onAdOptionsChange={setAdOptions}
                                    modelGeneration3DModels={modelGeneration3DModels}
                                    selectedModel3DModel={getSelectedModel3DModel(state.selectedTool)}
                                    onModel3DModelChange={handleModel3DModelChange}
                                    model3DParameterValues={getSelectedModel3DParameterValues(state.selectedTool)}
                                    onModel3DParameterValuesChange={handleModel3DParameterValuesChange}
                                    imageAspectRatio={state.selectedTool ? imageAspectRatios[state.selectedTool] : undefined}
                                    onImageAspectRatioChange={(value) => state.selectedTool && handleImageAspectRatioChange(state.selectedTool, value)}
                                />
                            </Suspense>
                        </ContentArea>
                    )}
                </>
            )}
        </Container>
    );
};

/**
 * Main AI Studio content component.
 * Wraps with Viewer3DProvider to enable 3D capture functionality.
 */
export const AIStudioContent: FC<AIStudioContentProps> = (props) => {
    return (
        <Viewer3DProvider>
            <AIStudioContentInner {...props} />
        </Viewer3DProvider>
    );
};

export default AIStudioContent;
